import { List } from 'immutable'
import React, { ReactElement } from 'react'

import Department from '../../model/department'
import { DateFormat } from '../../model/types'
import { formatAPIDate, getDate } from '../../utils/date-utils'
import { validateEmail } from '../../utils/email-utils'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import DatePicker from '../elements/date-picker'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Input from '../elements/input'
import Select from '../elements/select'
import Subtitle from '../elements/Subtitle'
import jsBrowserHistory from '../widgets/jsBrowserHistory'

type Props = {
  departments: List<Department>

  email?: string
  employmentStartDate: DateFormat
  departmentID?: string
}

type Fields = {
  email?: string
  employmentStartDate: Date
  departmentID?: string
}

export type InviteResult = {
  readonly step: 'Invite'
  email: string
  employmentStartDate: DateFormat
  departmentID?: string
}

function InviteForm(props: Props & FormComponentProps<Fields, InviteResult>): ReactElement | null {
  const { decorateField } = props

  return (
    <div>
      <div className="title-and-back-btn">
        <Subtitle>{t('freelancer.add.invite.title')}</Subtitle>
        <Button onClick={() => jsBrowserHistory.goBack()}>{t('general.back.button')}</Button>
      </div>

      <p>&nbsp;</p>
      {props.getFormError()}
      <Row>
        <Col span={12}>
          {decorateField('email', {
            placeholder: t('freelancer.add.invite.form.email'),
            validate: (val) => {
              if (!val) {
                return t('freelancer.add.invite.form.email.required')
              }
              if (!validateEmail(val)) {
                return t('freelancer.add.invite.form.email.invalid')
              }
              return null
            },
          })(<Input tabIndex={7} />)}
        </Col>
        <Col span={12}>
          {decorateField('employmentStartDate', {
            placeholder: t('freelancer.add.invite.form.employment_start_date'),
            validate: (val) => {
              if (!val) {
                return t('freelancer.add.invite.form.employment_start_date.required')
              }
              return null
            },
          })(<DatePicker allowClear={false} tabIndex={9} style={{ width: '100%' }} />)}
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          {decorateField('departmentID', {
            title: t('freelancer.add.invite.form.department_id'),
            placeholder: t('freelancer.add.invite.form.department_id.placeholder'),
          })(
            <Select mode="combobox" dropdownMatchSelectWidth={false} optionLabelProp="title">
              {props.departments
                .filter((department) => department.active)
                .map((department) => {
                  return (
                    <Select.Option key={department.id} value={department.id} title={department.name}>
                      {department.name}
                    </Select.Option>
                  )
                })}
            </Select>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Button
            htmlType="submit"
            size="extra-extra-large"
            type="primary"
            className="gtm-invite-employee"
            tabIndex={11}
          >
            {t('freelancer.add.invite.form.submit')}
          </Button>
        </Col>
      </Row>
    </div>
  )
}

export default withValidations<Props, Fields, InviteResult>({
  mapPropsToFields: (props) => ({
    email: props.email,
    employmentStartDate: getDate(props.employmentStartDate),
    departmentID: props.departmentID,
  }),
  onSubmit: (values) => ({
    step: 'Invite',
    email: values.email!,
    employmentStartDate: formatAPIDate(values.employmentStartDate),
    departmentID: values.departmentID,
  }),
})(InviteForm)
